// Singleton para gestionar una única conexión SSE a whatsapp-service
// Eventos emitidos: 'ready', 'message', 'error'

// Usar proxy del backend por defecto para ocultar el puerto del servicio
let base = import.meta.env.VITE_WHATSAPP_EVENTS_URL || (import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}whatsapp/events/` : '/api/whatsapp/events/');
let url = base;
let es = null;
let channelOpened = false;
let realChannelOpened = false; // sólo true cuando EventSource onopen real ocurre
let retryTimer = null;
let retryDelayMs = 1000; // backoff exponencial: 1s, 2s, 4s, máx 30s
const MAX_RETRY_MS = 30000;
const listeners = {
  ready: new Set(),
  message: new Set(),
  error: new Set(),
  connecting: new Set(),
  reconnecting: new Set(),
  channel: new Set(), // canal SSE abierto (onopen) pero sesión aún no lista
  stage: new Set(),   // estados intermedios: qr, authenticated, loading, etc.
  debug: new Set(),   // mensajes de depuración
};

let connectAttempts = 0;
let directTried = false;
let preflightDone = false;
let lastReadyEmitted = false;
let lastEventAt = 0; // timestamp del último evento útil recibido

async function preflightReadyCheck(label) {
  if (preflightDone || lastReadyEmitted) return;
  preflightDone = true;
  try {
  const apiBase = import.meta.env.VITE_API_URL || '/api/';
    const token = localStorage.getItem('access');
    const statusUrl = apiBase.endsWith('/') ? apiBase + 'whatsapp/status/' : apiBase + '/whatsapp/status/';
    console.debug('[SSE] preflight status fetch', label, statusUrl);
    const res = await fetch(statusUrl, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} });
    if (!res.ok) { console.debug('[SSE] preflight status !ok', res.status); return; }
    const data = await res.json();
    console.debug('[SSE] preflight data', data);
    if (data && data.stage) {
      emit('stage', { stage: data.stage, preflight: true });
      if (!channelOpened) { channelOpened = true; emit('channel', {}); }
      if (data.ready || data.stage === 'ready' || data.stage === 'authenticated') {
        lastReadyEmitted = true;
        emit('ready', { stage: data.stage, via: 'preflight' });
      }
    }
  } catch (e) {
    console.debug('[SSE] preflight error', e);
  }
}

function tryDirectConnect(reason) {
  try {
    if (directTried) return;
    directTried = true;
    console.debug('[SSE] tryDirectConnect reason=', reason);
    let clientIdParam = null;
    try {
      const parsed = new URL(url, window.location.href);
      clientIdParam = parsed.searchParams.get('clientId');
    } catch (_) {}
    if (!clientIdParam) {
      clientIdParam = (localStorage.getItem('username') || localStorage.getItem('userId') || '').trim();
    }
    const host = window.location.hostname || '127.0.0.1';
    const directBase = `http://${host}:3001/events`;
    const directUrl = clientIdParam ? `${directBase}?clientId=${encodeURIComponent(clientIdParam)}` : directBase;
    console.debug('[SSE] switching to direct URL', directUrl);
    try { if (es) { es.close(); } } catch(_) {}
    es = null; channelOpened = false;
    url = directUrl; // persistir para debug
    es = new EventSource(directUrl);
  es.onopen = () => { console.debug('[SSE] onopen (direct)'); channelOpened = true; realChannelOpened = true; emit('channel', {}); clearRetry(); preflightReadyCheck('direct'); };
    es.addEventListener('ready', (ev) => { try { const data = JSON.parse(ev.data); console.debug('[SSE] event:ready (direct)', data); emit('ready', data); } catch { emit('ready', {}); } clearRetry(); });
    es.addEventListener('status', (ev) => { try { const data = JSON.parse(ev.data); lastEventAt = Date.now(); console.debug('[SSE] event:status (direct)', data); if (!channelOpened) { channelOpened = true; emit('channel', {}); } if (data && data.stage) emit('stage', data); if (data && (data.stage === 'ready' || data.stage === 'authenticated')) { lastReadyEmitted = true; emit('ready', { stage: data.stage }); } } catch {} });
    es.addEventListener('message', (ev) => { try { lastEventAt = Date.now(); emit('message', JSON.parse(ev.data)); } catch {} });
    es.onerror = (err2) => {
      const now = Date.now();
      const age = now - lastEventAt;
      // Si hemos recibido algún evento útil en los últimos 5s, suprimir reconexión (glitch de red menor)
      if (lastEventAt && age < 5000) {
        console.debug('[SSE] onerror (direct) suprimido por actividad reciente age=', age);
        return;
      }
      console.debug('[SSE] onerror (direct) scheduling reconnect age=', age, err2);
      emit('error', err2);
      scheduleReconnect();
    };
  } catch (e2) {
    console.debug('[SSE] tryDirectConnect exception', e2);
  }
}

function emit(event, payload) {
  if (listeners[event]) {
    listeners[event].forEach((fn) => {
      try { fn(payload); } catch (_) {}
    });
  }
}

export function setEventsUrl(newUrl) {
  url = addTokenIfMissing(newUrl);
  // Reiniciar conexión en siguiente connect() si ya estaba abierta
  disconnect();
}

export function getEventsUrl() {
  return url;
}

export function connect() {
  if (es) return es;
  try {
    const finalUrl = addTokenIfMissing(url);
    emit('connecting', {});
    connectAttempts += 1;
    console.debug('[SSE] connect attempt #', connectAttempts, '->', finalUrl);
    channelOpened = false;
  realChannelOpened = false;
    preflightDone = false;
    lastReadyEmitted = false;
    es = new EventSource(finalUrl); // quitamos withCredentials: usamos token en query
    es.onopen = () => {
      console.debug('[SSE] onopen channel established');
      emit('channel', {});
      channelOpened = true;
      realChannelOpened = true;
      preflightReadyCheck('proxy');
    };
    es.addEventListener('ready', (ev) => {
      try {
        const data = JSON.parse(ev.data);
        console.debug('[SSE] event:ready data=', data);
        lastReadyEmitted = true;
        emit('ready', data);
      } catch { emit('ready', {}); }
      clearRetry();
    });
    // Algunos estados llegan como 'status' con stage: authenticated / ready
    es.addEventListener('status', (ev) => {
      try {
        const data = JSON.parse(ev.data);
        lastEventAt = Date.now();
        console.debug('[SSE] event:status', data);
        if (!channelOpened) { channelOpened = true; emit('channel', { synthetic:true }); }
        if (data && data.stage) { emit('stage', data); }
        if (data && (data.stage === 'ready' || data.stage === 'authenticated')) { lastReadyEmitted = true; emit('ready', { stage: data.stage }); }
      } catch {}
    });
    es.addEventListener('message', (ev) => { try { lastEventAt = Date.now(); emit('message', JSON.parse(ev.data)); } catch { /* ignorar */ } });
    es.onerror = (err) => {
      const now = Date.now();
      const age = now - lastEventAt;
      console.debug('[SSE] onerror', err, 'ageSinceLastEvent=', age, 'realChannelOpened=', realChannelOpened);
      if (!realChannelOpened && !directTried) {
        tryDirectConnect('proxy_onerror_before_real_open');
        return;
      }
      // Suprimir reconexión si hubo eventos recientes (<5s)
      if (lastEventAt && age < 5000) {
        console.debug('[SSE] onerror suprimido por actividad reciente age=', age);
        return;
      }
      emit('error', err);
      scheduleReconnect();
    };
    // Fallback rápido: si en 800ms no se abrió canal y no hubo error, intentar conexión directa
    setTimeout(() => {
      try {
        if (realChannelOpened || directTried) return;
        console.debug('[SSE] fallback: onopen real no ocurrió en 800ms, probando directo');
        tryDirectConnect('timeout_no_real_open');
      } catch (e2) { console.debug('[SSE] fallback direct exception', e2); }
    }, 800);
  } catch (e) {
    console.debug('[SSE] connect exception', e);
    emit('error', e);
  }
  return es;
}

export function disconnect() {
  if (es) {
    try { es.close(); } catch (_) {}
    es = null;
  }
  clearRetry();
}

export function reconnect() {
  // Utilizado por el botón "Reintentar"
  try {
    disconnect();
    retryDelayMs = 1000;
    connect();
  } catch (_) {}
}

export function on(event, handler) {
  if (!listeners[event]) return () => {};
  listeners[event].add(handler);
  // Asegurar conexión
  connect();
  return () => off(event, handler);
}

export function off(event, handler) {
  if (!listeners[event]) return;
  listeners[event].delete(handler);
}

export default {
  connect,
  disconnect,
  reconnect,
  on,
  off,
  setEventsUrl,
  getEventsUrl,
  forceStage, // uso interno para fallback basado en polling si SSE bloqueado
};

// Helpers
function addTokenIfMissing(u) {
  try {
    if (!u) return u;
    // Si ya trae token, no tocar
    if (u.includes('token=')) return u;
    const token = localStorage.getItem('access');
    if (!token) return u;
    const sep = u.includes('?') ? '&' : '?';
    return `${u}${sep}token=${encodeURIComponent(token)}`;
  } catch {
    return u;
  }
}

function scheduleReconnect() {
  try {
    if (retryTimer) return;
    // Cerrar si está abierto
    if (es) { try { es.close(); } catch (_) {} es = null; }
    const plannedIn = retryDelayMs;
    console.debug('[SSE] scheduling reconnect in', plannedIn, 'ms');
    emit('reconnecting', { inMs: plannedIn });
    retryTimer = setTimeout(() => {
      retryTimer = null;
      try { connect(); } catch (_) {}
      // Incrementar backoff hasta el máximo
      retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_MS);
    }, retryDelayMs);
  } catch (_) {}
}

function clearRetry() {
  try {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    retryDelayMs = 1000;
  } catch (_) {}
}

// Fallback sintético cuando el polling detecta stage pero el canal SSE nunca abrió (CORS/mixed-content bloqueado)
function forceStage(stage) {
  try {
    if (channelOpened) return; // ya hay canal real
    if (!stage) return;
    console.debug('[SSE] forceStage synthetic stage=', stage);
    channelOpened = true;
    emit('channel', { synthetic: true });
    emit('stage', { stage, synthetic: true });
    if (stage === 'ready' && !lastReadyEmitted) {
      lastReadyEmitted = true;
      emit('ready', { stage: 'ready', synthetic: true });
    }
  } catch (_) {}
}

// Reconectar automáticamente si el token cambia (para actualizar query ?token=...)
try {
  window.addEventListener('auth:token_refreshed', () => {
    try { disconnect(); } catch(_) {}
    try { connect(); } catch(_) {}
  });
  window.addEventListener('auth:logout', () => {
    try { disconnect(); } catch(_) {}
  });
} catch(_) {}
